import { API_ORIGIN } from '../config';
const API_BASE = `${API_ORIGIN}/api`;

const dynamicFormsService = {
  // Create client admin with dynamic form
  registerClientAdmin: async (data) => {
    const response = await fetch(`${API_BASE}/client-admin-auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return { ...result, status: response.status };
  },

  // Create form with optional payment link
  createForm: async (formData) => {
    const response = await fetch(`${API_BASE}/forms/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });
    return response.json();
  },

  // Add field to form
  addField: async (formId, fieldData) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/fields/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fieldData),
    });
    return response.json();
  },

  // Get all fields for a form
  getFields: async (formId) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/fields`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Update field
  updateField: async (formId, fieldId, fieldData) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/fields/${fieldId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fieldData),
    });
    return response.json();
  },

  // Delete field
  deleteField: async (formId, fieldId) => {
    const response = await fetch(`${API_BASE}/forms/fields/${formId}/${fieldId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Soft delete field (sets isDeleted = 1)
  softDeleteField: async (fieldId) => {
    const response = await fetch(`${API_BASE}/forms/fields/${fieldId}/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete field');
    }
    
    return response.json();
  },

  // Update field orders (bulk update after drag-drop)
  updateFieldOrders: async (formId, fieldsWithOrders) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/fields/reorder`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fields: fieldsWithOrders }),
    });
    return response.json();
  },

  // Add/Update layout
  updateLayout: async (formId, layoutData) => {
    // If layoutData is FormData, don't set Content-Type (browser will set it with boundary)
    const isFormData = layoutData instanceof FormData;
    
    const response = await fetch(`${API_BASE}/forms/${formId}/layout`, {
      method: 'POST',
      ...(isFormData ? {} : {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(layoutData),
      }),
      ...(isFormData && { body: layoutData }),
    });
    return response.json();
  },

  // Get layout
  getLayout: async (formId) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/layout`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Toggle form active status
  toggleFormActive: async (clientAdminId) => {
    const response = await fetch(`${API_BASE}/form/client-admin/${clientAdminId}/toggle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Create form for existing client admin
  createFormForClient: async (data) => {
    const response = await fetch(`${API_BASE}/form/client-admin/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Get complete form data by slug (for public form rendering)
  getFormBySlug: async (slug) => {
    const response = await fetch(`${API_BASE}/forms/by-slug/${slug}/complete`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch form');
    }
    
    return response.json();
  },

  // Submit form data
  submitForm: async (formId, data) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ data }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit form');
    }
    
    return response.json();
  },

  // Submit form data with files (multipart)
  submitFormMultipart: async (formId, formData) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/submit-multipart`, {
      method: 'POST',
      credentials: 'include',
      body: formData, // Don't set Content-Type header - browser will set it with boundary
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit form');
    }
    
    return response.json();
  },

  // Workshop Management Endpoints
  
  // Sync workshops for a field (set up capacity for options)
  syncWorkshops: async (formId, fieldId, workshops) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/workshops/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ field_id: fieldId, workshops }),
    });
    return response.json();
  },

  // Update workshop max capacity
  updateWorkshopCapacity: async (formId, workshopId, maxPax) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/workshops/${workshopId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ max_pax: maxPax }),
    });
    return response.json();
  },

  // Reset workshop current_pax to 0
  resetWorkshopPax: async (formId, workshopId) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/workshops/${workshopId}/reset-pax`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Delete (soft-delete) a workshop
  deleteWorkshop: async (formId, workshopId) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/workshops/${workshopId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Get all workshops with capacity info
  getWorkshops: async (formId) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/workshops`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Get booking list for one workshop
  getWorkshopBookings: async (formId, workshopId) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/workshops/${workshopId}/bookings`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  // Get all bookings across all workshops
  getAllWorkshopBookings: async (formId) => {
    const response = await fetch(`${API_BASE}/forms/${formId}/workshops/all-bookings`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },
};

export default dynamicFormsService;
